import { JoinProFormController, SeoController } from '@application/components';
import { JOIN_PRO_INFO_ROUTE_PER_PRO_PER_TYPE } from '@application/constants';
import { useModal, useRouter } from '@application/utils';
import { JoinProFormModal } from '@presentation/components';
import { PLACEHOLDER_IMAGE } from '@presentation/constants';
import type {
  JoinProInfoPageFaqData,
  JoinProInfoPageHeaderData,
  JoinProInfoPageMoreCardData,
  JoinProInfoPageSectionData,
} from '@presentation/pages';
import { JoinProInfoPage, JoinProInfoPageType } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { CARD_IMAGE_PER_TYPE, SECTION_IMAGES_PER_TYPE } from './assets';
import type { JoinProInfoPageControllerProps } from './types';

export * from './types';

export const JoinProInfoPageController: FC<JoinProInfoPageControllerProps> = (props) => {
  const { type, proType } = props;
  const { modal, show, close } = useModal();

  const { routeLocationDescriptor } = useRouter();
  const { translate } = useTranslation({ keyPrefix: 'joinPro.info' });
  const { translate: translateWithType } = useTranslation({
    keyPrefix: `joinPro.info.${type}`,
  });

  const otherTypes = useMemo(() => {
    return Object.keys(JoinProInfoPageType).filter(
      (key) => key !== type,
    ) as Array<JoinProInfoPageType>;
  }, [type]);

  const moreCards = useMemo((): JoinProInfoPageMoreCardData[] => {
    return otherTypes.map((infoType) => ({
      link: routeLocationDescriptor(JOIN_PRO_INFO_ROUTE_PER_PRO_PER_TYPE[proType][infoType]),
      image: CARD_IMAGE_PER_TYPE[infoType],
      title: translate(`more.cards.${infoType}.title`),
    }));
  }, [otherTypes, proType, routeLocationDescriptor, translate]);

  const header = useMemo((): JoinProInfoPageHeaderData => {
    return {
      title: translateWithType('header.title'),
      subtitle: translateWithType('header.subtitle'),
    };
  }, [translateWithType]);

  const sections = useMemo((): JoinProInfoPageSectionData[] => {
    const items = Object.values(
      translateWithType('sections', { returnObjects: true }) ?? {},
    ) as any[];
    return items.map((item, index) => {
      const texts = Object.values(item.texts ?? {}) as string[];
      return {
        image: SECTION_IMAGES_PER_TYPE[type][index] ?? PLACEHOLDER_IMAGE,
        title: item.title,
        subtitle: item.subtitle ?? undefined,
        texts,
      };
    });
  }, [translateWithType, type]);

  const faqItems = useMemo((): JoinProInfoPageFaqData[] | undefined => {
    const itemsMap = translateWithType('faq.items', { returnObjects: true, defaultValue: '' });
    if (!itemsMap) return undefined;
    const items = Object.values(itemsMap) as any[];
    return items.map((item, index) => {
      return {
        title: item.title,
        text: item.text,
      };
    });
  }, [translateWithType]);

  return (
    <>
      <SeoController
        title={translateWithType('seo.title')}
        description={translateWithType('seo.description')}
      />
      <JoinProInfoPage
        proType={proType}
        type={type}
        onButtonClick={() => show()}
        moreCards={moreCards}
        header={header}
        sections={sections}
        faqItems={faqItems}
      />
      <JoinProFormModal
        modal={modal}
        type={proType}
        formComponent={<JoinProFormController type={proType} onSubmit={close} />}
      />
    </>
  );
};
